import type { Annotation } from "@/types/annotation";
import { Prediction } from "@utils/api/inference";
import { Thumbnail } from "./CardThumbnails";

/**
 * Filters items (annotations or predictions) to only include those from the specified file
 */
export function filterByFileId<T extends { fileId?: string }>(
    items: T[],
    currentFileId: string | undefined
): T[] {
    return items.filter((item) => item?.fileId === currentFileId);
}

/**
 * Filters items (annotations or predictions) to only include those from the specified file AND frame
 * This is important for videos where a file can have multiple frames
 */
export function filterByFileAndFrameId<T extends { fileId?: string; frameId?: string }>(
    items: T[],
    currentFileId: string | undefined,
    currentFrameId: string | undefined
): T[] {
    return items.filter(
        (item) => item?.fileId === currentFileId && item?.frameId === currentFrameId
    );
}

/**
 * Converts annotations to thumbnail objects for display
 */
export function annotationsToThumbnails(
    annotations: (Annotation & { frameId: string; fileId: string })[]
): Thumbnail[] {
    return annotations
        .filter((anno) => anno?.thumbnailDataUrl)
        .map((anno) => ({
            thumbnailDataUrl: anno.thumbnailDataUrl!,
            id: anno.id,
            frameId: anno.frameId,
            fileId: anno.fileId
        }));
}

/**
 * Converts predictions to thumbnail objects for display
 */
export function predictionsToThumbnails(predictions: Prediction[]): Thumbnail[] {
    return predictions
        .filter((prediction) => prediction?.thumbnailDataUrl)
        .map((prediction) => ({
            thumbnailDataUrl: prediction.thumbnailDataUrl!,
            id: prediction.detection_id,
            fileId: prediction.fileId,
            frameId: prediction.frameId
        }));
}

/**
 * Counts annotations with real dimensions (excludes empty placeholders)
 */
export function countRealAnnotations(annotations: Annotation[]): number {
    return annotations.filter((anno) => anno.width > 0 && anno.height > 0).length;
}

/**
 * Checks if any annotations in the array have real dimensions (not placeholders)
 */
export function hasRealAnnotations(annotations: Annotation[]): boolean {
    return countRealAnnotations(annotations) > 0;
}

/**
 * Checks if a label has empty objects (annotations exist but none have real dimensions)
 */
export function hasEmptyObjectsForLabel(allAnnotations: Annotation[], labelName: string): boolean {
    const annotationsForLabel = allAnnotations.filter((anno) => anno?.label === labelName);
    return annotationsForLabel.length > 0 && !hasRealAnnotations(annotationsForLabel);
}

/**
 * Deletes annotations with the given label from the annotations map.
 * If annotations exist in other files, only deletes from the current file.
 * If annotations only exist in the current file, deletes from all files.
 */
export function deleteAnnotationsByLabel<T extends { label: string }>(
    annotationsMap: Record<string, Record<string, T[]>>,
    labelName: string,
    currentFileId: string | undefined
): Record<string, Record<string, T[]>> {
    const updated = { ...annotationsMap };

    // Check if annotations with this label exist in other files
    const hasAnnotationsInOtherFiles = Object.keys(updated).some((fileId) => {
        if (fileId === currentFileId) return false; // Skip current file
        return Object.keys(updated[fileId] || {}).some((frameId) => {
            return updated[fileId][frameId].some((annotation) => annotation.label === labelName);
        });
    });

    if (hasAnnotationsInOtherFiles) {
        // If annotations exist in other files, only delete from current file
        if (currentFileId && updated[currentFileId]) {
            Object.keys(updated[currentFileId]).forEach((frameId) => {
                updated[currentFileId][frameId] = updated[currentFileId][frameId].filter(
                    (annotation) => annotation.label !== labelName
                );
            });
        }
    } else {
        // If annotations only exist in current file, delete from all files
        Object.keys(updated).forEach((fileId) => {
            Object.keys(updated[fileId] || {}).forEach((frameId) => {
                updated[fileId][frameId] = updated[fileId][frameId].filter(
                    (annotation) => annotation.label !== labelName
                );
            });
        });
    }

    return updated;
}

/**
 * Completely deletes a label from all files/frames in the annotations map
 */
export function deleteAnnotationsByLabelCompletely<T extends { label: string }>(
    annotationsMap: Record<string, Record<string, T[]>>,
    labelName: string
): Record<string, Record<string, T[]>> {
    const updated = { ...annotationsMap };

    Object.keys(updated).forEach((fileId) => {
        Object.keys(updated[fileId] || {}).forEach((frameId) => {
            updated[fileId][frameId] = updated[fileId][frameId].filter(
                (annotation) => annotation.label !== labelName
            );
        });
    });

    return updated;
}
